import React, { useState } from "react";
import { X, Mail, Lock, User, ArrowRight, Loader2, AlertCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export function AuthModal({ isOpen, onClose, initialMode = "signin", onSuccess }) {
  const [mode, setMode] = useState(initialMode); // "signin" or "signup"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmationNotice, setConfirmationNotice] = useState(false);

  // Synchronize mode whenever modal opens or initialMode prop changes
  React.useEffect(() => {
    if (isOpen) {
      setMode(initialMode);
      setError(null);
      setConfirmationNotice(false);
    }
  }, [isOpen, initialMode]);

  const { signIn, signUp } = useAuth();


  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    setConfirmationNotice(false);

    try {
      if (mode === "signup") {
        const data = await signUp(email, password, { full_name: fullName });
        // If Supabase has email confirmation enabled, session might be null initially
        if (data?.user && !data?.session) {
          setConfirmationNotice(true);
          setLoading(false);
          return;
        }
      } else {
        await signIn(email, password);
      }

      setLoading(false);
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      setLoading(false);
      setError(err.message || "Authentication failed. Please try again.");
    }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setError(null);
    setConfirmationNotice(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="relative w-full max-w-md bg-gray-900 border border-gray-700/80 rounded-2xl shadow-2xl p-6 sm:p-8 overflow-hidden">
        {/* Decorative background glow */}
        <div className="absolute -top-24 -right-24 w-48 h-48 bg-purple-600/20 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-blue-600/20 rounded-full blur-3xl pointer-events-none"></div>

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white rounded-xl hover:bg-gray-800 transition-all"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Title */}
        <div className="text-center mb-6">
          <h3 className="text-2xl font-bold text-white mb-2">
            {mode === "signin" ? "Welcome Back" : "Access Casino Figures"}
          </h3>
          <p className="text-sm text-gray-400">
            {mode === "signin"
              ? "Sign in to access your casino performance dashboard"
              : "Create an account to start analyzing verified regulatory figures"}
          </p>
        </div>

        {/* Error notice */}
        {error && (
          <div className="mb-4 p-3.5 bg-red-900/40 border border-red-500/50 rounded-xl flex items-start gap-2.5 text-red-200 text-sm">
            <AlertCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Confirmation notice (if email verification is turned on in Supabase) */}
        {confirmationNotice && (
          <div className="mb-4 p-3.5 bg-blue-900/40 border border-blue-500/50 rounded-xl text-blue-200 text-sm">
            <p className="font-semibold mb-1">Check your email</p>
            <p>
              We've sent a verification link to <strong>{email}</strong>. Please confirm your email to access the dashboard.
            </p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === "signup" && (
            <div>
              <label className="block text-xs font-medium text-gray-300 uppercase tracking-wider mb-1.5">
                Full Name
              </label>
              <div className="relative">
                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  required
                  placeholder="John Smith"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}

                  className="w-full pl-11 pr-4 py-3 bg-gray-800/80 border border-gray-700 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-300 uppercase tracking-wider mb-1.5">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="email"
                required
                placeholder="name@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-gray-800/80 border border-gray-700 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-300 uppercase tracking-wider mb-1.5">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="password"
                required
                minLength={6}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-gray-800/80 border border-gray-700 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full mt-2 py-3.5 px-4 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white font-semibold rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-purple-600/25 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <span>{mode === "signin" ? "Sign In" : "Create Account"}</span>
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </button>
        </form>

        {/* Mode switcher toggle */}
        <div className="mt-6 text-center text-sm text-gray-400">
          {mode === "signin" ? (
            <p>
              Don't have an account?{" "}
              <button
                onClick={() => switchMode("signup")}
                className="text-purple-400 hover:text-purple-300 font-semibold underline underline-offset-2 transition-colors"
              >
                Sign up free
              </button>
            </p>
          ) : (
            <p>
              Already have an account?{" "}
              <button
                onClick={() => switchMode("signin")}
                className="text-purple-400 hover:text-purple-300 font-semibold underline underline-offset-2 transition-colors"
              >
                Sign in
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default AuthModal;
